import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, ExternalLink, Calendar, User, Save, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { TaskTimerButton } from '@/components/timetracking/TaskTimerButton';

interface TaskDetailDrawerProps {
  taskId: string | null;
  projectId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TaskRow {
  id: string;
  project_id: string;
  name: string;
  status: string | null;
  complexity: string | null;
  assignee: string | null;
  assigned_resource_id: string | null;
  start_date: string | null;
  end_date: string | null;
  description?: string | null;
}

interface ResourceLite {
  id: string;
  role: string;
  name: string | null;
}

const STATUSES = ['Pendiente', 'Por hacer', 'En progreso', 'En revisión', 'Completado'];
const COMPLEXITIES = ['Baja', 'Media', 'Alta'];

export const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  taskId,
  projectId,
  open,
  onOpenChange,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [task, setTask] = useState<TaskRow | null>(null);
  const [resources, setResources] = useState<ResourceLite[]>([]);

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('project_tasks')
        .select('*')
        .eq('id', taskId)
        .maybeSingle();
      if (!cancelled) {
        if (error) {
          toast({ title: 'Error', description: error.message, variant: 'destructive' });
        }
        setTask((data as TaskRow) ?? null);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, taskId, toast]);

  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      const { data } = await supabase
        .from('resource_allocations')
        .select('resources:resource_id(id, role, name)')
        .eq('project_id', projectId);
      const rows = (data ?? [])
        .map((r) => (r as { resources: ResourceLite | null }).resources)
        .filter((r): r is ResourceLite => !!r);
      setResources(rows);
    })();
  }, [open, projectId]);

  const update = (field: keyof TaskRow, value: string | null) => {
    setTask((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    if (!task) return;
    setSaving(true);
    const { error } = await supabase
      .from('project_tasks')
      .update({
        name: task.name,
        status: task.status,
        complexity: task.complexity,
        assignee: task.assignee,
        assigned_resource_id: task.assigned_resource_id,
        start_date: task.start_date,
        end_date: task.end_date,
      })
      .eq('id', task.id);
    setSaving(false);
    if (error) {
      toast({ title: 'Error al guardar', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Tarea actualizada' });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    onOpenChange(false);
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!confirm(`¿Eliminar la tarea "${task.name}"?`)) return;
    const { error } = await supabase.from('project_tasks').delete().eq('id', task.id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Tarea eliminada' });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalle de tarea</SheetTitle>
          <SheetDescription>Edita la información sin abandonar la vista.</SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && !task && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No se encontró la tarea.
          </p>
        )}

        {!loading && task && (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={task.name} onChange={(e) => update('name', e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={task.status ?? 'Pendiente'}
                  onValueChange={(v) => update('status', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Complejidad</Label>
                <Select
                  value={task.complexity ?? 'Media'}
                  onValueChange={(v) => update('complexity', v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COMPLEXITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1"><User className="h-3 w-3" /> Asignado</Label>
              <Select
                value={task.assigned_resource_id ?? 'unassigned'}
                onValueChange={(v) => update('assigned_resource_id', v === 'unassigned' ? null : v)}
              >
                <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Sin asignar</SelectItem>
                  {resources.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name || r.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Inicio</Label>
                <Input
                  type="date"
                  value={task.start_date ?? ''}
                  onChange={(e) => update('start_date', e.target.value || null)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Fin</Label>
                <Input
                  type="date"
                  value={task.end_date ?? ''}
                  onChange={(e) => update('end_date', e.target.value || null)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas / Asignado libre</Label>
              <Textarea
                value={task.assignee ?? ''}
                onChange={(e) => update('assignee', e.target.value)}
                placeholder="Notas, asignado externo…"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <TaskTimerButton taskId={task.id} />
              <Badge variant="outline" className="text-xs">{task.status || 'Pendiente'}</Badge>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/project-management/${task.project_id}?tab=tasks`);
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir
              </Button>
              <Button variant="ghost" size="icon" onClick={handleDelete} aria-label="Eliminar">
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
